import { CaseflowCaseTypes } from '@rabbit/bizproc/core';
import { PersonaTypeSingleLetter } from '@rabbit/data/types';
import {
  Config_ClaimActionsType,
  Config_ClaimCosts_LogType,
  Config_ClaimDetailsSectionType,
  Config_ClaimDetailViewType,
  Config_ClaimsHoldingForms,
  Config_ClaimsTableType,
  Config_CountryTaxLabel,
  Config_WarrantyContentSource,
  Config_WarrantyMergeStrategy,
  Config_DateDefaultBehavior,
  Config_PartnerTypeOptions,
  Config_RegistrationDetailsView_WarrantyDetailsSectionType,
  Config_RegistrationTableType,
  Config_UpdateClaimDetails_ClaimDetailsFormType,
  Config_UpdateClaimDetails_CustomerFormType,
  DefaultStoreName,
  PrintSummaryEnum,
  REGISTRATION_FIELDS,
  RestrictedUserPathsType,
  SAGE_ROUTE_NAME,
} from './types/config-types';

// Please read documentation/config.md for more info

const BaseConfig = {
  NAVIGATION: {
    SAGE: {
      DEFAULT_PATH: SAGE_ROUTE_NAME.REGISTRATIONS,
      DEFAULT_PARTNER_PATH: SAGE_ROUTE_NAME.REGISTRATIONS,
      PARTNER_RESTRICTED_PATH: [
        SAGE_ROUTE_NAME.CLAIMS,
        SAGE_ROUTE_NAME.MANAGE,
        SAGE_ROUTE_NAME.WARRANTY_OVERVIEW,
      ] as string[],
    },
    RESTRICTED: false,
    RESTRICTED_PATHS: [] as string[],
    RESTRICTED_USER_PATHS: {
      Warrantor: [],
      ExternalRepairer: [],
    } as RestrictedUserPathsType,
  },
  DASHBOARD: {
    OLIVE: {
      HIDE_MOMENT_BANNER: false,
    },
  },
  CUSTOMER: {
    ENABLED: true,
    CORPORATE_ACCOUNT: false,
    ALLOW_SAGE_ACCESS: false,
    SECONDARY_EMAIL: false,
    SECONDARY_PHONE: false,
    REG_ADDRESS_MANDATORY: false,
    CLAIM_ADDRESS_MANDATORY: false,
    /**
     * When true, consumers must have a phone number on their profile before they can create a claim (Olive).
     * This is enforced in the UI claim flow and in the backend case registration.
     */
    CLAIM_PHONE_MANDATORY: false,
    MANAGE: {
      REDIRECT_TO_REGISTRATION: false,
    },
    HIDE_ADDITIONAL_INFO: false,
    MARKETING_SIGNUP_INFO: {
      SHOW_TENANT_NAME: true,
      OPT_OUT_MARKETING: false,
      SHOW_PROMOTIONAL_EMAIL_FIELD: false,
    },
    ALLOW_CHANGE_CUSTOMER_EMAIL: false,
    SHOW_MARKETING_INFO: false,
    HIDE_CONSUMER_PREFERRED_CONTACT: true,
    ALGOLIA_SEARCH_ENABLED: false,
    USE_FALLBACK_FETCHIFY: true,
    USE_GOADDRESS_LOOKUP: false,
    PHONE_NUMBER_REQUIRED: true,
    COMPLETE_PROFILE: {
      PHONE_NUMBER_REQUIRED: false,
      ADDRESS_REQUIRED: false,
      AGE_BRACKET_REQUIRED: false,
      PROFILE_PROMPT_ENABLED: false,
    },
  },
  INSTALLATION_REQUIRED: false,
  CLAIMS: {
    CASEFLOW_TYPE: CaseflowCaseTypes.LITE,
    PROXY_OWNER_PERSONA: PersonaTypeSingleLetter.Warrantor,
    INTERNAL_REPAIRERS_ENABLED: false,
    CLAIMS_LIST_TABLE: {
      ENABLED: [
        PersonaTypeSingleLetter.Warrantor,
        PersonaTypeSingleLetter.Repairer,
      ] as PersonaTypeSingleLetter[],
      CAN_VIEW_ALL_CLAIMS: [
        PersonaTypeSingleLetter.Warrantor,
      ] as PersonaTypeSingleLetter[],
      ASSIGNED_UNASSIGNED_TABS: false,
    },
    OPERATING_PERSONAS: [
      PersonaTypeSingleLetter.Repairer,
      PersonaTypeSingleLetter.Warrantor,
    ],
    CLAIMS_FLOW: {
      CAN_ASSIGN_REPAIRER: [] as PersonaTypeSingleLetter[],
      SERIAL_NUMBER: false,
      INSTALLATION: false,
      SERIAL_NUMBER_PROOF: false,
      CREATE_WITH_WARRANTY_INFO: false,
      CREATE_HOLDING_FORM: 'BASE' as Config_ClaimsHoldingForms,
      CLAIMS_TABLE_TYPE: 'TYPE1' as Config_ClaimsTableType,
      NEW_CLAIM: {
        NEW_USER_BTN: true,
        NEW_PRODUCT_BTN: true,
        SERIAL_NUMBER: true,
        MILEAGE: false,
      },
      INVOICE: {
        PROFORMA: false,
        ENABLED: false,
      },
      PARTS_ASSESSMENT: false,
      CLAIM_UPLOAD: true,
      CREATE_PRODUCT: true,
    },
    CHAT: {
      IS_READONLY: false,
    },
    CLAIM_DETAIL_VIEW: {
      CLAIM_DETAILS: 'DEFAULT' as Config_ClaimDetailsSectionType,
      PRINTING: false,
      UPDATE_CLAIM_DETAILS: {
        ENABLED: [PersonaTypeSingleLetter.Warrantor],
        CUSTOMER_DETAILS: 'NONE' as Config_UpdateClaimDetails_CustomerFormType,
        CLAIM_DETAILS:
          'DEFAULT' as Config_UpdateClaimDetails_ClaimDetailsFormType,
        //ATTACHMENTS: 'DEFAULT' as UpdateClaimDetails_AttachmentsFormType,
      },
      CLAIM_ACTIONS: {
        ENABLED: [] as PersonaTypeSingleLetter[],
        TYPE: 'TYPE1' as Config_ClaimActionsType,
      },
      SHOW_REPAIR_ASSESSMENT: true,
      CLAIM_COSTS: {
        COST_LOGGING_FLOW: 'DEFAULT' as Config_ClaimCosts_LogType,
        SHOW_SECTION: [
          PersonaTypeSingleLetter.Warrantor,
        ] as PersonaTypeSingleLetter[],
        LIST_NON_CASE_ACTORS: false,
      },
      REPAIRER_DETAILS_SECTION: [] as PersonaTypeSingleLetter[],
      CUSTOMER_DETAILS_SECTION: [] as PersonaTypeSingleLetter[], // Empty array means all personas can see it
      PRODUCT_DETAILS_SECTION: [] as PersonaTypeSingleLetter[], // Empty array means all personas can navigate to product details
      TYPE: 'DEFAULT' as Config_ClaimDetailViewType,
      EDIT_PURCHASE_DATE: false,
    },
    CONSUMER: {
      BLOCK_CUSTOMER_EMAIL_NOTIFICATIONS: false,
      CAN_INITIATE_CLAIM: true,
      SHOW_CLAIM_INITIATION_CONTACT: false,
    },
  },
  AUTHENTICATION: {},
  ACCOUNT: {
    TAX_LABEL: Config_CountryTaxLabel.VAT as Config_CountryTaxLabel,
    DEFAULT_TAX_RATE: '', // percentage, has to be string for the form, do not ask me why... tech debt.. https://iwarranty.atlassian.net/browse/DEV-1941
  },
  HOLDINGS: {
    NOTES_ENABLED: false,
    NOTES_SECTION: false,
    ALLOW_CHANGE_CUSTOMER: false,
    SHOW_AUDIT_LOG: false,
    REGISTRATION: {
      TABLE_TYPE: Config_RegistrationTableType.DEFAULT,
      SERIAL_NUMBER_REQUIRED: false,
      ADVANCED_TABLE: false,
      SERIAL_NUMBER_HIDE: false,
      SERIAL_NUMBER_HELP: {
        ENABLED: false,
        TEXT: 'Locate the serial number on your product.',
      },
      SHOW_DEALER_GROUP: false,
      MANAGEMENT: {
        EDIT_REGISTRATION: false,
      },
      SHOW_PARTNER_REGISTRATION_WITH_SHARED_LOCATION: false,
      SHOW_MONTHLY_DATA: false,
      SHOW_DEALER_NAME: false,
      SHOW_PRICE_FOR_PARTNER: true,
      CAN_EDIT_PURCHASE_DATE: false,
      SHOW_CHASSIS_NUMBER: false,
      SHOW_REGISTRATION_NUMBER: false,
      SHOW_TABLE_SERIAL_NUMBER: true,
    },
    NEW_REGISTRATION_FLOW: {
      NEW_USER_BTN: true,
      ADD_NEW_PRODUCT: false,
      UNIQUE_PRODUCT: false,
      SUBMISSION_PREVIEW: false,
      PROOF_OF_PURCHASE: false,
      RRP: false,
      INTERNAL_COMMENT: false,
      EXTRA_FIELDS: [] as any,
      DEFAULT_STORE: false,
      ADD_MANUAL_PRODUCT: false,
      STORES_FROM_FIRESTORE_RETAILERS: false,
      SHOW_STORES_FROM_BUSINESS_INVITES: false,
      VENDABLE_FROM_FIRESTORE: false,
      SEARCH_VENDABLES_FROM_FIRESTORE: false,
      SEARCH_VENDABLES_BY_BRANDS: false,
      DEFAULT_STORE_NAME: 'store' as DefaultStoreName,
      SCAN_QR_CODE: false,
    },
    UPDATE_REGISTRATION_FLOW: {
      HIDE_PURCHASE_DATE: false,
      HIDE_STORE: false,
    },
    MANDATORY_FIELDS: {
      PURCHASE_TIME: false,
      PURCHASE_PRICE: true,
    },
    DATE_DEFAULTS: {
      PURCHASE_TIME_DEFAULT: Config_DateDefaultBehavior.CURRENT_DATE,
      WARRANTY_START_DATE_DEFAULT: Config_DateDefaultBehavior.CURRENT_DATE,
    },
    REGISTRATION_DETAILS_VIEW: {
      WARRANTY_DETAILS_SECTION:
        'DEFAULT' as Config_RegistrationDetailsView_WarrantyDetailsSectionType,
      SERIAL_NUMBER: true,
      PURCHASE_PRICE: true,
      SHOW_RRP_DELTA: false,
      PRODUCT_DOCUMENTATION: false,
      PROOF_OF_PURCHASE: true,
      COMPANY_NAME: false,
      SHOW_USER_MANUAL: false,
    },
    PRODUCT: {
      CUSTOM_CATEGORY: false,
      SHOW_PRODUCTS_FIRST: false, // If true, show products directly; if false, show suggestions first
      APPEND_BRAND_TO_NAME: false,
    },
    INSTALLATION_REQUIRED: false,
    CONSUMER: {
      TYPE_OF_CONSUMER: false,
    },
    DATE_FORMAT: 'dd/MM/yyyy',
    SERIAL_NUMBER_VALIDATION: {
      ENABLED: false,
    },
  },
  VENDABLES: {
    EDITOR: {
      ENABLED: [] as PersonaTypeSingleLetter[],
      CAN_ADD_NEW: [] as PersonaTypeSingleLetter[],
    },
    PART_SEARCH: {
      ENABLED: false,
    },
    CUSTOM_FIELDS: {
      RRP: true,
      DEPRECATED: true,
      [REGISTRATION_FIELDS.CHASSIS_NUMBER]: false,
      [REGISTRATION_FIELDS.REGISTRATION_NUMBER]: false,
      [REGISTRATION_FIELDS.PRODUCTION_DATE]: false,
      [REGISTRATION_FIELDS.MILEAGE]: false,
    },
    MANDATORY_FIELDS: {
      UPC: true,
    },
    BRAND_SELECTABLE: false,
    SHOW_ONLY_BRAND_PRODUCTS: false,
  },
  MAIN_GROUP: {
    ENABLED: false,
  },
  MAKE_TAB: {
    ENABLED: false,
  },
  MODEL_TAB: {
    ENABLED: false,
  },
  WARRANTIES: {
    MANAGEMENT: {
      SHOW_WARRANTY_OPTIONS_BTN: true,
      CAN_VOID_WARRANTIES: false,
      PARTNER_CAN_VOID_WARRANTIES: false,
      CAN_EDIT_WARRANTIES: false,
      CAN_EDIT_PRICE_PLANS: false,
      CAN_SET_WARRANTY_STATUS: false,
      CAN_SET_PART_STATUS: false,
    },
    WARRANTY_TC_FROM_FIRESTORE: false,
    // Source selection for warranty content on Olive vendable preview
    // 'template' | 'vendable' | 'merge'
    WARRANTY_TC_CONTENT_SOURCE:
      Config_WarrantyContentSource.TEMPLATE as Config_WarrantyContentSource,
    // Merge strategy only used when CONTENT_SOURCE === 'merge'
    // 'append' - vendable content appended after template content
    // 'prepend' - vendable content before template content
    // 'override-duplicates' - merge by title, vendable items override matching titles
    MERGE_STRATEGY:
      Config_WarrantyMergeStrategy.APPEND as Config_WarrantyMergeStrategy,
    SHOW_FULL_WARRANTY_TC: false,
    SHOW_CLAIM_LIMIT: true,
  },
  MARKETING: {
    EMAIL_TEMPLATE_LANGUAGE: 'EN',
  },
  SHOPIFY: {
    ENABLED: false,
  },
  PARTNERS: {
    ENABLED: false,
    PARTNER_TYPE_OPTIONS: Config_PartnerTypeOptions.DEFAULT,
    REGISTRATION: {
      TC_REQUIRED: false,
      SIGNUP_TC_REQUIRED: false,
      ADDRESS_REQUIRED: false,
      PARTNER_BRAND_MAPPING: false,
    },
    PARTNER_TABLE: {
      MAINGROUP: false,
      SHOW_LAST_LOGIN_TIME: false,
    },
    EDIT_PARTNER: {
      EDIT_BUSINESS_USER_INVITE: false,
      RESEND_MAIL: false,
    },
    SHOW_STORE_ID: false,
  },
  LANGUAGE: {
    SHOW_FULL_LABEL: false,
    LABELLING: false,
  },
  PRINT_SUMMARY: {
    TITLE: 'Warranty' as PrintSummaryEnum,
    SHOW_CLAIM_LIMIT: true,
    SHOW_LABOUR_RATE: true,
    SHOW_WARRANTY_TERM_CONDITIONS: true,
  },
  CATEGORY: {
    ENABLED: false,
    MANAGEMENT: {
      ENABLED: false,
      CAN_ADD_NEW: false,
      CAN_EDIT: false,
      CAN_DELETE: false,
    },
  },
  LEGAL: {
    PRIVACY_POLICY_URL: '',
  },
  // Off for every tenant. A tenant being migrated to the new platform turns it
  // on and supplies the destination URL, so the prompt appears only where a
  // migration is actually running.
  PLATFORM_MIGRATION: {
    ENABLED: false,
    URL: '',
  },
};

export default BaseConfig;
